import React, { useEffect, useRef, useState, useReducer, useCallback, forwardRef, useImperativeHandle } from 'react';
import { flushSync } from 'react-dom';
import './DetailsCard.css';
import './detailscardSliders.css';
import TopSightsCarousel from './TopSightsCarousel';
import { functionUrls } from './services/firebaseFunctions';
import HistoricalPeriods from './HistoricalPeriods';
import AudioStatusWidget from './AudioStatusWidget';
import { useDisplayLocation } from './signals/displayLocation';
import QuotaModal from './QuotaModal';
import { playHistorySuctionAnimation } from './utils/historySuctionAnimation';
import ThinkingIndicator from './components/ThinkingIndicator';
import SigninPopup from './SigninPopup';
import { loginMachine, useLoginStore } from './auth/loginMachine';

// NOTE: Top-Sights CSV fetching, caching and enrichment now live exclusively
// in TopSightsCarousel.js.  Any old helpers/constants have been removed from
// DetailsCard to avoid duplicate logic.

// Helper to fetch language links for a Wikipedia article
async function fetchArticleLangLinks(title, sourceArticleLang, signal) {
  const WIKI_API_BASE = `https://${sourceArticleLang}.wikipedia.org/w/api.php`;
  const params = new URLSearchParams({
    action: 'query',
    titles: title,
    prop: 'langlinks',
    lllimit: 'max', // Get all language links
    format: 'json',
    origin: '*',
  });
  const url = `${WIKI_API_BASE}?${params.toString()}`;
  console.log(`[DetailsCard/LangLinks] Fetching langlinks for "${title}" from ${sourceArticleLang}.wikipedia.org`);

  const resp = await fetch(url, { signal });
  if (!resp.ok) throw new Error(`Wikipedia API error (langlinks): ${resp.status} for ${url}`);
  const data = await resp.json();

  const pages = data.query?.pages;
  if (!pages) return [];
  const pageId = Object.keys(pages)[0];
  if (!pageId || !pages[pageId].langlinks) return [];

  return pages[pageId].langlinks.map(link => ({
    lang: link.lang,
    title: link['*'] // Title in the target language
  }));
}

// Helper to fetch a short introduction (extract) for a Wikipedia article
async function fetchArticleExtract(title, targetDisplayLang, signal) {
  const WIKI_API_BASE = `https://${targetDisplayLang}.wikipedia.org/w/api.php`;
  const params = new URLSearchParams({
    action: 'query',
    titles: title,
    prop: 'extracts',
    exintro: 'true',    // Get only content before the first section
    explaintext: 'true', // Get plain text, not HTML
    format: 'json',
    origin: '*',
  });
  const url = `${WIKI_API_BASE}?${params.toString()}`;
  console.log(`[DetailsCard/Extract] Fetching extract for "${title}" from ${targetDisplayLang}.wikipedia.org`);

  const resp = await fetch(url, { signal });
  if (!resp.ok) throw new Error(`Wikipedia API error (extracts): ${resp.status} for ${url}`);
  const data = await resp.json();

  const pages = data.query?.pages;
  if (!pages) return null;
  const pageId = Object.keys(pages)[0];
  // Check for invalid page or missing extract (e.g. -1 pageid for non-existent page)
  if (!pageId || pages[pageId].missing !== undefined || pages[pageId].invalid !== undefined) {
    console.warn(`[DetailsCard/Extract] Page "${title}" in lang "${targetDisplayLang}" might be missing or invalid. Response:`, pages[pageId]);
    return null;
  }
  return pages[pageId].extract || null;
}

async function fetchWikidataSitelinks(qid, signal) {
  const params = new URLSearchParams({
    action: 'wbgetentities',
    format: 'json',
    ids: qid,
    props: 'sitelinks',
    origin: '*',
  });

  const url = `https://www.wikidata.org/w/api.php?${params.toString()}`;
  console.log(`[DetailsCard/Wikidata] Fetching sitelinks for ${qid}`);

  const resp = await fetch(url, { signal });
  if (!resp.ok) throw new Error(`Wikidata API error (sitelinks): ${resp.status} for ${url}`);

  const data = await resp.json();
  const entity = data?.entities?.[qid];
  return entity?.sitelinks || {};
}

async function fetchWikidataPopulation(qid, signal) {
  const params = new URLSearchParams({
    action: 'wbgetentities',
    format: 'json',
    ids: qid,
    props: 'claims',
    origin: '*',
  });

  const url = `https://www.wikidata.org/w/api.php?${params.toString()}`;
  console.log(`[DetailsCard/Wikidata] Fetching population for ${qid}`);

  const resp = await fetch(url, { signal });
  if (!resp.ok) throw new Error(`Wikidata API error (population): ${resp.status} for ${url}`);

  const data = await resp.json();
  const claims = data?.entities?.[qid]?.claims?.P1082 || [];

  const values = claims
    .map((claim) => claim?.mainsnak?.datavalue?.value?.amount)
    .map((amount) => Number(String(amount ?? '').replace('+', '')))
    .filter((num) => Number.isFinite(num) && num > 0);

  if (!values.length) return null;
  return Math.max(...values);
}

/**
 * Sliding bottom card similar to Google Maps.
 * Shows either a Wikipedia article (default) or the current location info
 * depending on toggle button state.
 */
const DetailsCard = forwardRef(({
  article,
  viewportWikiLanguage = 'en',
  userLanguageLabel = 'English',
  onClose,
  startNarration,
  onSightsReady,
  onArticleSelected = () => {},
  history,
  historyIndex,
  goBack,
  goForward,
  initialShowLocationSights = false,
  gpsAvailable = null, // NEW: tells us if device has a GPS fix
  userPreferredWikiLangCode = 'en', // User's preferred language for descriptions
  isGuestAccount = true,
  showPwaInstallButton = false,
  onPwaInstallClick,
  onControlsRender, // NEW: callback to render controls externally
  fromSearchfield = false, // NEW: indicates if current location came from search field
  searchFieldLocation = null, // NEW: full search field result for location param
  onSignInClick = () => {}, // NEW: callback to show sign-in popup
  searchFieldRequestRequired = false, // NEW: indicates if search field request still needs to be made
  onSearchFieldRequestCompleted = () => {}, // NEW: callback when search field request is completed
  cityName, // New prop for city name
  prefetchedLocationPopulation = null,
  onHeightChange,
  audioEnabled = false,
  audioStatus = 'idle',
  audioTitle = '',
  audioMode = 'overview',
  onAudioPauseToggle,
  onAudioTogglePlayPause,
  onAudioStop,
  onAudioSeek,
  audioCurrentTime = 0,
  audioDuration = 0,
  onGoToMyLocation, // NEW: callback to center map on user GPS position
  onToggleMapType, // NEW: callback to toggle map style (satellite/roadmap)
  onOpenUserHistory, // NEW: callback to open user history popup
  isMapCenteredOnUser = false, // NEW: whether map is already centered on user
}, ref) => {
  const MIN_HEIGHT_VH = 15; // Min 30%
  const MAX_HEIGHT_VH = 98; // Max 90%
  const INITIAL_HEIGHT_VH = 30; // Open at 30%
  const LOGIN_PROMPT_MIN_HEIGHT_VH = 45;
  const BOTTOM_CONTROLS_HEIGHT = 0; // Bottom external controls removed
  // Mid point (roughly half-expanded) used for snap logic
  const MID_HEIGHT_VH = (MIN_HEIGHT_VH + MAX_HEIGHT_VH) / 2; // e.g. ~52.5

  // Get locationData from signal
  const locationData = useDisplayLocation();
  const loginUi = useLoginStore((state) => state.ui);
  const loginAccount = useLoginStore((state) => state.account);
  const showCardLoginPrompt = loginUi.kind === 'DetailsCardLoginPrompt' || loginUi.kind === 'LoginBusy';
  const isLoginAnonymous = loginAccount.kind === 'AnonAccount' || loginAccount.kind === 'AnonLoginPending';

  // State to track position when location button was last pressed
  const [lastButtonPressPosition, setLastButtonPressPosition] = useState(null);
  const initialPositionSetRef = useRef(false); // Track if we've set the initial position
  const historyButtonRef = useRef(null);
  const audioWidgetAnchorRef = useRef(null);

  useEffect(() => {
    const onAudioCompleted = (evt) => {
      const mode = evt?.detail?.mode;
      const label = mode === 'anecdote' ? 'Anecdote' : 'History';
      playHistorySuctionAnimation({
        sourceEl: audioWidgetAnchorRef.current,
        targetEl: historyButtonRef.current,
        label,
      });
    };

    window.addEventListener('audioCompleted', onAudioCompleted);
    return () => window.removeEventListener('audioCompleted', onAudioCompleted);
  }, []);

  // Calculate distance between two coordinates in km using Haversine formula
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    if (!lat1 || !lon1 || !lat2 || !lon2) return 0;
    const R = 6371; // Radius of Earth in km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a =
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  };

  // Check if we've moved more than 5km since last button press
  const hasMovedSignificantly = () => {
    if (!lastButtonPressPosition || !locationData) return false;
    const currentLat = locationData.lat;
    const currentLon = locationData.lon;
    const distance = calculateDistance(
      lastButtonPressPosition.lat,
      lastButtonPressPosition.lon,
      currentLat,
      currentLon
    );
    return distance > 5; // 5km threshold
  };

  const [heightPx, setHeightPx] = useState(0);
  // Reference to root element to read its background colour
  const cardRef = useRef(null);
  const [cardBgColor, setCardBgColor] = useState('#1d1d1d');

  // Grab background colour once after mount (assumes it does not change dynamically)
  useEffect(() => {
    if (!cardRef.current) return;
    const bg = window.getComputedStyle(cardRef.current).backgroundColor;
    if (bg) setCardBgColor(bg);
  }, []);

  // Snapshot of locationData specifically for the TopSightsCarousel
  // This prevents re-triggering CSV fetches in TopSightsCarousel if locationData changes for other reasons
  // (e.g. minor GPS updates) UNLESS it's an explicit request (initial load or button press)
  const [sightsLocationSnapshot, setSightsLocationSnapshot] = useState(null);
  const [sightsWikiLanguageSnapshot, setSightsWikiLanguageSnapshot] = useState(viewportWikiLanguage); // New state for viewportWikiLanguage snapshot
  const [showLocationSights, setShowLocationSights] = useState(initialShowLocationSights);

  // Track search field usage for topsights requests
  const [sightsFromSearchfield, setSightsFromSearchfield] = useState(fromSearchfield);
  const [sightsSearchFieldLocation, setSightsSearchFieldLocation] = useState(searchFieldLocation);
  const [sightsSearchFieldRequestRequired, setSightsSearchFieldRequestRequired] = useState(searchFieldRequestRequired);

  const normalizeTopicIdPart = (value) => String(value || '').trim().toLowerCase().replace(/\s+/g, ' ');

  function getLocationName(loc) {
    if (!loc) return '';
    if (loc.topic_place_name) return loc.topic_place_name;

    const addr = loc.address;
    if (addr) {
      const fromAddr = addr.city || addr.town || addr.village || addr.hamlet || addr.county || addr.state || addr.country;
      if (fromAddr) return fromAddr;
    }

    return loc.display_name || addr?.display_name || loc.name || '';
  }

  const makeArticleTopicId = (articleInput) => {
    const pageId = articleInput?.pageid;
    if (pageId) return `article:${pageId}`;
    return `article:title:${normalizeTopicIdPart(articleInput?.title || '')}`;
  };

  const makeLocationTopicId = (loc) => {
    const stableName = normalizeTopicIdPart(getLocationName(loc));
    if (stableName) return `location:name:${stableName}`;

    const qid = normalizeTopicIdPart(loc?.topic_wikidata || '');
    if (qid) return `location:qid:${qid}`;

    const lat = Number(loc?.lat);
    const lon = Number(loc?.lon);
    if (Number.isFinite(lat) && Number.isFinite(lon)) {
      return `location:coords:${lat.toFixed(3)},${lon.toFixed(3)}`;
    }

    return 'location:unknown';
  };

  const topicReducer = (state, action) => {
    console.log('[DetailsCard/topicReducer] PrevState:', state, 'Action:', action);
    switch (action.type) {
      case 'SET_ARTICLE': {
        return {
          ...state,
          type: 'article',
          article: action.payload,
          id: makeArticleTopicId(action.payload),
        };
      }
      // Manual switch requested by user (location button / map background)
      case 'SET_LOCATION_MANUAL': {
        const incomingLocId = makeLocationTopicId(action.payload);
        const keepId = state.type === 'location' && state.id && incomingLocId === state.id;
        return {
          ...state,
          type: 'location',
          locationData: action.payload,
          id: keepId ? state.id : incomingLocId,
        };
      }
      // Automatic one-off switch when GPS fix becomes available
      case 'SET_LOCATION_AUTO_INITIAL': {
        if (state.gpsInitialHandled) return state; // already did the initial auto-switch
        return {
          ...state,
          type: 'location',
          locationData: action.payload,
          id: makeLocationTopicId(action.payload),
          gpsInitialHandled: true,
        };
      }
      default:
        return state;
    }
  };

  const [topic, dispatch] = useReducer(topicReducer, {
    id: article ? makeArticleTopicId(article) : makeLocationTopicId(locationData),
    type: article ? 'article' : 'location',
    article: article || null,
    locationData: locationData || null,
    gpsInitialHandled: false,
  });
  // Which tab is currently selected within the menu (overview | history | eras | food | wiki)
  // null means the user has not selected a tab yet – used to show guidance text.
  const [activeTab, setActiveTab] = useState(null);
  // Which tab group is selected: 'audio' (Audio Guides) or 'more'
  const [activeTabGroup, setActiveTabGroup] = useState('audio');

  const [images, setImages] = useState([]);
  const [imagesLoading, setImagesLoading] = useState(false);
  const [imagesError, setImagesError] = useState(null);

  // New state for image pagination/virtualization
  const [displayedImageCount, setDisplayedImageCount] = useState(20); // Increased from 6 to 20 for better content display
  const [imageLoadingMore, setImageLoadingMore] = useState(false);

  // -----------------------------
  // Results stack – vertical slider containing dynamic content
  // -----------------------------
  const [stack, setStack] = useState([]); // each item: { type: 'imageGrid' | 'period', data }
  const imagesAddedRef = useRef(false);
  const sightsAddedRef = useRef(false);
  const [imageDisplayCount, setImageDisplayCount] = useState(9);

  // Cache for results stack per topic and language
  const stackCacheRef = useRef(new Map());
  const prevTopicKeyRef = useRef(null);

  // Food-related state
  const [foodDishes, setFoodDishes] = useState([]);
  const [foodLoading, setFoodLoading] = useState(false);
  const [foodError, setFoodError] = useState(null);
  const [showQuotaModal, setShowQuotaModal] = useState(false);

  // -----------------------------
  // Local Food caching per location
  // -----------------------------
  // Map key: loc:<locationName>|userLang:<userLanguageLabel>|wikiLang:<viewportWikiLanguage>
  const foodCacheRef = useRef(new Map());
  const prevFoodTopicKeyRef = useRef(null);

  // Restore or clear cached local food dishes when the topic (location/article) or language changes
  useEffect(() => {
    const makeFoodKey = (t) => {
      if (t.type !== 'location') return null;
      const locName = getLocationName(t.locationData);
      return `loc:${locName}|userLang:${userLanguageLabel}|wikiLang:${viewportWikiLanguage}`;
    };

    // Save current dishes to cache if we have a previous key
    if (prevFoodTopicKeyRef.current) {
      foodCacheRef.current.set(prevFoodTopicKeyRef.current, foodDishes);
    }

    const newKey = makeFoodKey(topic);
    prevFoodTopicKeyRef.current = newKey;

    if (newKey && foodCacheRef.current.has(newKey)) {
      // Restore dishes for this location/language
      setFoodDishes(foodCacheRef.current.get(newKey));
    } else {
      // Fresh location or not a location topic → clear list so fetch starts anew
      setFoodDishes([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topic, viewportWikiLanguage, userLanguageLabel]);

  // Keep cache updated whenever dishes change for the current location
  useEffect(() => {
    if (topic.type !== 'location') return;
    const locName = getLocationName(topic.locationData);
    const key = `loc:${locName}|userLang:${userLanguageLabel}|wikiLang:${viewportWikiLanguage}`;
    foodCacheRef.current.set(key, foodDishes);
  }, [foodDishes, topic, viewportWikiLanguage, userLanguageLabel]);

  // Image Popup State
  const [isPopupVisible, setIsPopupVisible] = useState(false);
  const [popupImageIndex, setPopupImageIndex] = useState(0);

  // Article Description State
  const [articleDescription, setArticleDescription] = useState('');
  const [descriptionLoading, setDescriptionLoading] = useState(false);
  const [descriptionError, setDescriptionError] = useState(null);

  const draggingRef = useRef(false);
  const startYRef = useRef(0);
  const startXRef = useRef(0); // NEW: track initial X to distinguish horizontal vs vertical pans
  const startHeightRef = useRef(0);
  const pendingDragRef = useRef(false); // NEW: candidate drag state until threshold passed
  // Keep recent pointer moves (last ~120 ms) to calculate flick velocity
  const recentMovesRef = useRef([]);

  const locationTopicWikidataQid =
    topic.type === 'location'
      ? (topic.locationData?.topic_wikidata || null)
      : null;

  // Keep Wikidata QID anchored to the CURRENT DetailsCard location topic.
  // We only adopt a resolved QID from global location data when the place names match.
  useEffect(() => {
    if (topic.type !== 'location') return;

    const currentQid = topic.locationData?.topic_wikidata;
    const incomingQid = locationData?.topic_wikidata;
    if (currentQid || !incomingQid) return;

    const normalize = (value) => String(value || '').trim().toLowerCase();
    const topicName = normalize(getLocationName(topic.locationData));
    const incomingName = normalize(locationData?.topic_place_name || getLocationName(locationData));

    const looksSamePlace = topicName && incomingName && (
      topicName === incomingName ||
      topicName.includes(incomingName) ||
      incomingName.includes(topicName)
    );

    if (!looksSamePlace) return;

    dispatch({
      type: 'SET_LOCATION_MANUAL',
      payload: {
        ...(topic.locationData || {}),
        topic_wikidata: incomingQid,
        topic_population: locationData?.topic_population ?? topic.locationData?.topic_population ?? null,
        topic_place_name: locationData?.topic_place_name || getLocationName(topic.locationData),
      },
    });
  }, [topic, locationData]);

  const hasLocationWikiAvailable = topic.type === 'location' && !!locationTopicWikidataQid;
  const [locationPopulation, setLocationPopulation] = useState(null);
  const [locationPopulationLoading, setLocationPopulationLoading] = useState(false);
  const populationCacheRef = useRef(new Map());

  const formatPopulationCompact = (value) => {
    if (!Number.isFinite(value)) return null;
    return new Intl.NumberFormat('en', { notation: 'compact', maximumFractionDigits: 1 }).format(value);
  };

  useEffect(() => {
    if (topic.type !== 'location' || !locationTopicWikidataQid) {
      setLocationPopulation(null);
      setLocationPopulationLoading(false);
      return;
    }

    const cachedPopulation = populationCacheRef.current.get(locationTopicWikidataQid);
    if (Number.isFinite(cachedPopulation) && cachedPopulation > 0) {
      setLocationPopulation(cachedPopulation);
      setLocationPopulationLoading(false);
      return;
    }

    const prefetchedPopulation =
      topic.locationData?.topic_population ??
      locationData?.topic_population ??
      prefetchedLocationPopulation;

    if (Number.isFinite(prefetchedPopulation) && prefetchedPopulation > 0) {
      populationCacheRef.current.set(locationTopicWikidataQid, prefetchedPopulation);
      setLocationPopulation(prefetchedPopulation);
      setLocationPopulationLoading(false);
      return;
    }

    const controller = new AbortController();
    setLocationPopulationLoading(true);
    fetchWikidataPopulation(locationTopicWikidataQid, controller.signal)
      .then((population) => {
        if (controller.signal.aborted) return;
        if (Number.isFinite(population) && population > 0) {
          populationCacheRef.current.set(locationTopicWikidataQid, population);
        }
        setLocationPopulation(population);
      })
      .catch((err) => {
        if (err.name === 'AbortError') return;
        console.error('[DetailsCard/Wikidata] Population fetch failed:', err);
        setLocationPopulation(null);
      })
      .finally(() => {
        if (controller.signal.aborted) return;
        setLocationPopulationLoading(false);
      });

    return () => controller.abort();
  }, [topic.type, topic.locationData, locationData, locationTopicWikidataQid, prefetchedLocationPopulation]);

  // -----------------------------------------------------------
  // Wikipedia iframe state (dark-theme, language fall-back)
  // -----------------------------------------------------------
  const [wikipediaUrl, setWikipediaUrl] = useState(null);
  const [wikiIframeLoading, setWikiIframeLoading] = useState(false);
  const [wikiIframeError, setWikiIframeError] = useState(null);

  // Reset iframe when topic changes
  useEffect(() => {
    setWikipediaUrl(null);
    setWikiIframeError(null);
  }, [topic]);

  // -----------------------------
  // Top-sights UI logic
  // -----------------------------
  // 1. showLocationSights – controls visibility on screen
  // 2. sightsLocationSnapshot – immutable snapshot passed to the carousel so
  //    it does NOT refresh when the map updates locationData implicitly.
  useEffect(() => {
    if (initialShowLocationSights && locationData && !sightsLocationSnapshot) {
      console.log('[DetailsCard] Auto-show Top-Sights at startup. Setting location and language snapshots');
      setShowLocationSights(true);
      setSightsLocationSnapshot(locationData);
      setSightsWikiLanguageSnapshot(viewportWikiLanguage);
      setSightsFromSearchfield(fromSearchfield);
      setSightsSearchFieldLocation(searchFieldLocation);
      setSightsSearchFieldRequestRequired(searchFieldRequestRequired);
    }
  }, [initialShowLocationSights, locationData, sightsLocationSnapshot, viewportWikiLanguage]);

  useEffect(() => {
    if (gpsAvailable && locationData && !topic.gpsInitialHandled) {
      console.log('[DetailsCard] GPS fix detected – auto-switching to location once.');
      dispatch({ type: 'SET_LOCATION_AUTO_INITIAL', payload: locationData });
    }
  }, [gpsAvailable, locationData, topic.gpsInitialHandled]);

  useEffect(() => {
    if (topic.type === 'location' && topic.locationData) {
      console.log('[DetailsCard] Topic changed to LOCATION:', getLocationName(topic.locationData));
      // If the topic switched to location (e.g. via button press or initial auto switch),
      // ensure the sights carousel is shown and has the latest location data for its fetches.
      setShowLocationSights(true);
      setSightsLocationSnapshot(topic.locationData);
      setSightsWikiLanguageSnapshot(viewportWikiLanguage);

      // No automatic data fetching here for location, TopSightsCarousel handles its own internal fetches
      // based on sightsLocationSnapshot.
    }
  }, [topic, viewportWikiLanguage]);

  // Removed automatic topic update when location changes
  // The topic should only change when the user explicitly clicks the location button
  // This prevents the topic from automatically changing when the derived location name updates

  // Set initial height when opening
  useEffect(() => {
    const availableHeight = window.innerHeight - BOTTOM_CONTROLS_HEIGHT;
    const init = (INITIAL_HEIGHT_VH / 100) * availableHeight;
    setHeightPx(init);
  }, []);

  // Set initial GPS position as the reference point for green dot
  useEffect(() => {
    if (!initialPositionSetRef.current && locationData && locationData.lat && locationData.lon) {
      console.log('[DetailsCard] Setting initial GPS position for green dot reference');
      setLastButtonPressPosition({
        lat: locationData.lat,
        lon: locationData.lon
      });
      initialPositionSetRef.current = true;
    }
  }, [locationData]);

  // Log current height in VH whenever it changes
  useEffect(() => {
    const availableHeight = window.innerHeight - BOTTOM_CONTROLS_HEIGHT;
    const vh = (heightPx / availableHeight) * 100;
    console.log(`[DetailsCard] current height: ${vh.toFixed(1)}vh (available: ${availableHeight}px)`);
  }, [heightPx]);

  // Report card height to parent (used for external controls alignment)
  useEffect(() => {
    if (typeof onHeightChange === 'function') {
      onHeightChange(heightPx);
    }
  }, [heightPx, onHeightChange]);

  useEffect(() => {
    if (!showCardLoginPrompt) return;
    const availableHeight = window.innerHeight - BOTTOM_CONTROLS_HEIGHT;
    const targetHeight = (LOGIN_PROMPT_MIN_HEIGHT_VH / 100) * availableHeight;
    setHeightPx((current) => (current < targetHeight ? targetHeight : current));
  }, [showCardLoginPrompt]);

  // Add / cleanup global pointer listeners when dragging
  useEffect(() => {
    function handleMove(e) {
      // Decide between SCROLL vs DRAG when gesture is still undecided
      if (gestureModeRef.current === 'pending') {
        const dx = e.clientX - startXRef.current;
        const dy = startYRef.current - e.clientY;
        const ADMIT_THRESHOLD = 8; // px

        if (Math.abs(dy) > ADMIT_THRESHOLD && Math.abs(dy) > Math.abs(dx) * 1.1) {
          // Vertical intention confirmed.

          // If gesture started inside content column and it can scroll in the direction,
          // prefer scrolling.
          if (insideContentStartRef.current && contentRef.current) {
            const el = contentRef.current;
            const atTop = el.scrollTop <= 0;
            const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight;

            // dy > 0  -> finger moves up  -> scroll down (content moves up)
            // dy < 0  -> finger moves down -> scroll up (content moves down)
            const wantsScrollDown = dy > 0;

            const canScroll = (wantsScrollDown && !atBottom) || (!wantsScrollDown && !atTop);

            if (canScroll) {
              gestureModeRef.current = 'scroll';
              pendingDragRef.current = false;
              // no need to set draggingRef; scrolling handled below
            } else {
              gestureModeRef.current = 'drag';
              draggingRef.current = true;
              try { e.target.setPointerCapture?.(e.pointerId); } catch {}
              document.body.style.userSelect = 'none';
            }
          } else {
            // outside scroll area – always drag
            gestureModeRef.current = 'drag';
            draggingRef.current = true;
            try { e.target.setPointerCapture?.(e.pointerId); } catch {}
            document.body.style.userSelect = 'none';
          }
        } else if (Math.abs(dx) > ADMIT_THRESHOLD && Math.abs(dx) > Math.abs(dy)) {
          // horizontal pan – cancel potential drag/scroll
          pendingDragRef.current = false;
          gestureModeRef.current = 'none';
        }
      }

      // Handle manual SCROLL mode
      if (gestureModeRef.current === 'scroll') {
        const dy = startYRef.current - e.clientY; // positive when moving up
        const el = contentRef.current;
        if (el) {
          const newScroll = startScrollTopRef.current + dy;
          el.scrollTop = Math.max(0, Math.min(el.scrollHeight - el.clientHeight, newScroll));
        }
        // prevent pull-to-refresh / rubber-band
        e.preventDefault();
        return;
      }

      // Existing DRAG behaviour
      if (!draggingRef.current) return;
      e.preventDefault();
      e.stopPropagation(); // Prevent map from panning while dragging card
      const dy = startYRef.current - e.clientY;
      let newHeight = startHeightRef.current + dy;
      const availableHeight = window.innerHeight - BOTTOM_CONTROLS_HEIGHT;
      const min = (MIN_HEIGHT_VH / 100) * availableHeight;
      const max = (MAX_HEIGHT_VH / 100) * availableHeight;
      newHeight = Math.max(min, Math.min(max, newHeight));
      setHeightPx(newHeight);

      // Track recent pointer positions for velocity calculation (keep last ~120 ms)
      const now = performance.now();
      recentMovesRef.current.push({ y: e.clientY, time: now });
      // Trim moves older than 120 ms to focus on recent velocity
      while (recentMovesRef.current.length > 2 && now - recentMovesRef.current[0].time > 120) {
        recentMovesRef.current.shift();
      }
    }
    function stopDrag(e) {
      if (draggingRef.current) {
        e?.stopPropagation();
      }

      // ---------------------
      // Snap-to-points logic
      // ---------------------
      if (draggingRef.current) {
        const availableHeight = window.innerHeight - BOTTOM_CONTROLS_HEIGHT;
        const min = (MIN_HEIGHT_VH / 100) * availableHeight;
        const max = (MAX_HEIGHT_VH / 100) * availableHeight;
        const mid = (MID_HEIGHT_VH / 100) * availableHeight;

        const moves = recentMovesRef.current;
        if (moves.length >= 2) {
          const first = moves[0];
          const last = moves[moves.length - 1];
          const dy = last.y - first.y;          // +ve ⇒ downwards, -ve ⇒ upwards
          const dt = last.time - first.time || 1; // ms
          const velocity = dy / dt;              // px/ms

          const FLICK_UP_THRESHOLD = -0.7;       // fast upward flick
          const FLICK_DOWN_HIGH = 1.0;           // very fast downward flick
          const FLICK_DOWN_MED  = 0.7;           // moderate downward flick

          let targetHeight = null;

          if (velocity < FLICK_UP_THRESHOLD) {
            // Fast flick upwards → fully expand
            targetHeight = max;
          } else if (velocity > FLICK_DOWN_HIGH) {
            // Very fast downward flick → collapse to minimum
            targetHeight = min;
          } else if (velocity > FLICK_DOWN_MED) {
            // Moderate downward flick → snap to mid position
            targetHeight = mid;
          }

          if (targetHeight !== null) {
            setHeightPx(targetHeight);
          }
        }
      }

      draggingRef.current = false;
      pendingDragRef.current = false; // reset candidate state
      gestureModeRef.current = 'none';
      document.body.style.userSelect = '';
    }
    window.addEventListener('pointermove', handleMove);
    window.addEventListener('pointerup', stopDrag);
    window.addEventListener('pointercancel', stopDrag);
    return () => {
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', stopDrag);
      window.removeEventListener('pointercancel', stopDrag);
    };
  }, []);

  const onCardPointerDown = (e) => {
    // Native scrolling is disabled; gesture logic decides whether to scroll or drag.

    pendingDragRef.current = true;
    startYRef.current = e.clientY;
    startXRef.current = e.clientX;
    startHeightRef.current = heightPx;
    recentMovesRef.current = [{ y: e.clientY, time: performance.now() }];

    // Special case: If the gesture starts on the Top-Sights carousel we want the card to drag
    // rather than scroll the column.  Treat such pointers as "outside" the scrollable area.
    const startedInsideSightsCarousel = e.target.closest('.sights-carousel-container');

    insideContentStartRef.current =
      contentRef.current &&
      contentRef.current.contains(e.target) &&
      !startedInsideSightsCarousel;

    if (insideContentStartRef.current) {
      startScrollTopRef.current = contentRef.current.scrollTop;
    }

    // Reset gesture mode state for this new pointer interaction
    gestureModeRef.current = 'pending';
  };

  // When a new article arrives (e.g. user clicked a different place), switch topic to that article
  useEffect(() => {
    if (article) {
      dispatch({ type: 'SET_ARTICLE', payload: article });
      console.log(`[DetailsCard] Article clicked. Wiki Page ID: ${article.pageid}`);
      // Log the Wikipedia pageviews for debugging and analytics (if available)
      if (article.pvSum !== undefined) {
        console.log(`[DetailsCard] Pageviews for "${article.title}": ${article.pvSum}`);
      }
    }
  }, [article?.pageid]);

  // -------------------------------------------------------------
  // Detect when the currently displayed article has been cleared
  // (e.g. user tapped the map background) and switch to LOCATION.
  // -------------------------------------------------------------
  const prevArticleIdRef = useRef(article?.pageid || null);
  useEffect(() => {
    const prev = prevArticleIdRef.current;
    const curr = article?.pageid || null;
    if (prev && !curr && locationData && topic.type !== 'location') {
      console.log('[DetailsCard] Article cleared – switching topic to current location.');
      dispatch({ type: 'SET_LOCATION_MANUAL', payload: locationData });
    }
    prevArticleIdRef.current = curr;
  }, [article, locationData, topic.type]);

  // Keep the selected tab stable across topic metadata updates.
  // (Do not auto-reset here; users reported Historical Eras disappearing
  // while interacting with period buttons.)

  // Helper to get optimal image size based on device
  const getOptimalImageSize = () => {
    const isMobile = window.innerWidth <= 768;
    const isTablet = window.innerWidth <= 1024;

    if (isMobile) return 250; // Reduced from 300
    if (isTablet) return 300; // Reduced from 400
    return 350; // Reduced from 500
  };

  // Fetch images when article changes - SIMPLIFIED VERSION
  useEffect(() => {
    if (!article?.title) {
      console.log('No article title, skipping image fetch');
      setImages([]);
      setDisplayedImageCount(20); // Reset pagination
      return;
    }

    const articleSourceLang = article?.sourceWikiLang || viewportWikiLanguage;
    const wikiDomain = `${articleSourceLang}.wikipedia.org`;
    console.log(`Fetching images for article: "${article.title}" from ${wikiDomain}`);
    const controller = new AbortController();

    setImages([]); // Clear previous images immediately
    setDisplayedImageCount(20); // Reset pagination
    setImagesLoading(true);
    setImagesError(null);

    fetchWikipediaArticleImagesSimplified(article.title, wikiDomain, controller.signal)
      .then((imgs) => {
        if (controller.signal.aborted) return;
        console.log(`Received ${imgs.length} images for article "${article.title}"`);
        setImages(imgs);
      })
      .catch((err) => {
        if (err.name === 'AbortError') return;
        console.error(`Image fetch error for "${article.title}"`, err);
        setImagesError('could not connect to image service');
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setImagesLoading(false);
        }
      });

    return () => controller.abort();
  }, [article?.title, article?.sourceWikiLang, viewportWikiLanguage]);

  // Reset stack when topic or language changes (with caching)
  useEffect(() => {
    // Helper to generate unique cache key
    const makeKey = (t) => {
      const topicId = t.type === 'article'
        ? `article:${t.article?.pageid}`
        : `location:${getLocationName(t.locationData)}`;
      return `${topicId}|userLang:${userLanguageLabel}|wikiLang:${viewportWikiLanguage}`;
    };

    // Cache the previous stack
    if (prevTopicKeyRef.current) {
      stackCacheRef.current.set(prevTopicKeyRef.current, stack);
    }

    // Compute key for new topic and language
    const newKey = makeKey(topic);
    prevTopicKeyRef.current = newKey;

    // Restore if cached, else clear for fresh load
    const cached = stackCacheRef.current.get(newKey);
    if (cached) {
      setStack(cached);
      imagesAddedRef.current = true;
      sightsAddedRef.current = true;
      setImageDisplayCount(9);
      // restore cached food slider if any (no need for explicit ref)
    } else {
      setStack([]);
      imagesAddedRef.current = false;
      sightsAddedRef.current = false;
      setImageDisplayCount(9);
      // fresh slate for new topic (food slider will be added on user action)
    }
    // We intentionally KEEP any existing top-sight markers on the map when
    // switching away from the location view so users can still see them while
    // browsing individual articles.
  }, [topic, userLanguageLabel, viewportWikiLanguage, onSightsReady]);

  /* ------------------------------------------------------------------ */
  /* Image-grid injection removed – we now use the horizontal slider    */
  /* ------------------------------------------------------------------ */
  useEffect(() => {
    if (!imagesLoading && images.length && !imagesAddedRef.current) {
      imagesAddedRef.current = true;
      // Images are shown in the slider; no need to push an imageGrid item
      // setImageDisplayCount(9);
      // setStack((prev) => [{ type: 'imageGrid' }, ...prev]);
    }
  }, [imagesLoading, images.length]);

  // Handle periods click using new component
  const handlePeriodsClick = () => {
    // Ensure card is dragged to at least 70vh
    const availableHeight = window.innerHeight - BOTTOM_CONTROLS_HEIGHT;
    const currentVh = (heightPx / availableHeight) * 100;
    const targetVh = 70;

    if (currentVh < targetVh) {
      const targetHeight = (targetVh / 100) * availableHeight;
      setHeightPx(targetHeight);
    }
  };

  // -------------------------
  // Image Popup Handlers
  // -------------------------
  const handleImageClick = (index) => {
    setPopupImageIndex(index);
    setIsPopupVisible(true);
  };

  const handlePopupClose = () => {
    setIsPopupVisible(false);
  };

  const handlePopupPrev = () => {
    setPopupImageIndex((prevIndex) =>
      prevIndex === 0 ? images.length - 1 : prevIndex - 1
    );
  };

  const handlePopupNext = () => {
    setPopupImageIndex((prevIndex) =>
      prevIndex === images.length - 1 ? 0 : prevIndex + 1
    );
  };

  // ------------------------------------------------------------------
  // Helper – derive a medium-resolution variant (~1000px wide) from the
  // Wikimedia thumbnail URL.  This keeps pop-ups crisp without pulling the
  // multi-megabyte original file.
  // ------------------------------------------------------------------
  const POPUP_IMAGE_WIDTH = 600;  // px – lighter yet still larger than slider

  // -----------------------------
  // New refs for manual scroll vs drag handling
  // -----------------------------
  const contentRef = useRef(null);           // .content-column element
  const gestureModeRef = useRef('none');     // 'pending' | 'scroll' | 'drag'
  const startScrollTopRef = useRef(0);       // initial scrollTop when gesture starts
  const insideContentStartRef = useRef(false); // did the gesture start inside content?

  const getPopupImageUrl = useCallback((imgObj) => {
    if (!imgObj) return '';

    // Prefer the explicit thumb (small) URL if present, otherwise fallback
    // to whatever `url` we have (could be original).
    const baseUrl = imgObj.thumb || imgObj.url || '';

    // Wikimedia thumbnail URLs contain the pattern:
    //   /thumb/<hash1>/<hash2>/<Filename>/<size>px-<Filename>
    // We swap the <size> segment to POPUP_IMAGE_WIDTH while preserving the
    // rest of the path. If the regex does not match we simply return the
    // original URL (better to show *something* than nothing).
    return baseUrl.replace(
      /(\/thumb\/[^/]+\/[^/]+\/[^/]+\/)(\d+)px-/,
      `$1${POPUP_IMAGE_WIDTH}px-`
    );
  }, []);

  /* --------------------------------------------------------------------
   * Prefetch medium-resolution images in the background so pop-ups appear
   * instantly without blocking initial rendering.  We limit the number of
   * images and schedule them during idle periods (or delayed timeouts as a
   * fallback).
   * ------------------------------------------------------------------*/
  useEffect(() => {
    if (!images || images.length === 0) return;

    const MAX_PREFETCH = 8; // arbitrarily chosen – tweak for balance
    const urlsToPrefetch = images.slice(0, MAX_PREFETCH)
      .map(getPopupImageUrl)
      .filter(Boolean);

    const createdLinks = [];

    const enqueue = (href, delay) => {
      const createLink = () => {
        // Guard against duplicates
        if (document.querySelector(`link[rel="prefetch"][href="${href}"]`)) return;
        const l = document.createElement('link');
        l.rel = 'prefetch';
        l.as = 'image';
        l.href = href;
        document.head.appendChild(l);
        createdLinks.push(l);
      };

      if ('requestIdleCallback' in window) {
        requestIdleCallback(createLink, { timeout: 3500 });
      } else {
        setTimeout(createLink, delay);
      }
    };

    urlsToPrefetch.forEach((u, idx) => enqueue(u, 1500 + idx * 500));

    // Cleanup in case component unmounts before all links load
    return () => {
      createdLinks.forEach(l => l.parentNode && l.parentNode.removeChild(l));
    };
  }, [images, getPopupImageUrl]);

  // ------------------------------
  // Wikipedia image helpers (SIMPLIFIED FOR PERFORMANCE)
  // ------------------------------
  const WIKI_API_PATH = '/w/api.php';

  // -------------------------------------------------
  // SIMPLIFIED: Fetch only essential images quickly
  // -------------------------------------------------
  async function fetchWikipediaArticleImagesSimplified(title, wikiDomain = 'en.wikipedia.org', signal) {
    console.log(`fetchWikipediaArticleImagesSimplified: Starting for "${title}" on ${wikiDomain}`);

    try {
      // First, get the lead/page image quickly
      const leadImage = await fetchLeadImageSimplified(title, wikiDomain, signal);

      if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');

      // Then get a small batch of article images
      const articleImages = await fetchBasicArticleImages(title, wikiDomain, signal);

      if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');

      // Combine and deduplicate
      const allImages = [];
      const seenUrls = new Set();

      // Add lead image first if it exists
      if (leadImage && leadImage.url) {
        allImages.push(leadImage);
        seenUrls.add(leadImage.url);
      }

      // Add other images, avoiding duplicates
      articleImages.forEach(img => {
        if (!seenUrls.has(img.url)) {
          allImages.push(img);
          seenUrls.add(img.url);
        }
      });

      console.log(`fetchWikipediaArticleImagesSimplified: Returning ${allImages.length} images`);
      return allImages;

    } catch (error) {
      if (error.name === 'AbortError') throw error;
      console.error('fetchWikipediaArticleImagesSimplified: Error', error);
      return [];
    }
  }

  // Simplified lead image fetch
  async function fetchLeadImageSimplified(title, wikiDomain, signal) {
    const optimalSize = getOptimalImageSize();
    const params = new URLSearchParams({
      action: 'query',
      titles: title,
      prop: 'pageimages',
      piprop: 'thumbnail|original',
      pithumbsize: optimalSize.toString(),
      format: 'json',
      origin: '*',
    });

    const apiUrl = `https://${wikiDomain}${WIKI_API_PATH}?${params.toString()}`;
    const resp = await fetch(apiUrl, { signal });

    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
    if (!resp.ok) return null;

    const data = await resp.json();
    const pages = data.query?.pages || {};
    const pageId = Object.keys(pages)[0];

    if (!pageId || !pages[pageId]?.thumbnail?.source) return null;

    const thumb = pages[pageId].thumbnail;
    const original = pages[pageId].original || {};
    return {
      url: original.source || thumb.source,   // high-res (fallback to thumb)
      thumb: thumb.source,                    // low-res for slider
      width: thumb.width || original.width || 0,
      height: thumb.height || original.height || 0,
    };
  }

  // Simplified article images fetch - get only first few images
  async function fetchBasicArticleImages(title, wikiDomain, signal) {
    const optimalSize = getOptimalImageSize();

    // Get limited image list
    const params = new URLSearchParams({
      action: 'query',
      titles: title,
      prop: 'images',
      format: 'json',
      origin: '*',
      imlimit: '25', // Increased from 10 to 25 to support showing up to 20 images
    });

    const apiUrl = `https://${wikiDomain}${WIKI_API_PATH}?${params.toString()}`;
    const resp = await fetch(apiUrl, { signal });

    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
    if (!resp.ok) return [];

    const data = await resp.json();
    const pages = data.query?.pages || {};
    const pageId = Object.keys(pages)[0];
    const images = pages[pageId]?.images || [];

    // Filter out unwanted images
    const filtered = images.filter((img) => {
      const t = img.title.toLowerCase();
      return !(
        t.includes('svg') ||
        t.includes('icon') ||
        t.includes('logo') ||
        t.includes('wikimedia') ||
        t.includes('commons') ||
        t.startsWith('file:icon')
      );
    }).slice(0, 20); // Take up to 20 images to support displaying 20

    if (!filtered.length) return [];

    // Get image URLs in smaller batches
    return await fetchImageUrlsSimplified(
      filtered.map(i => i.title),
      wikiDomain,
      signal,
      optimalSize
    );
  }

  // Simplified image URL fetching
  async function fetchImageUrlsSimplified(imageTitles, wikiDomain, signal, optimalSize) {
    const batchSize = 5; // Reduced from 20 to 5
    const urls = [];

    for (let i = 0; i < imageTitles.length; i += batchSize) {
      if (signal?.aborted) break;

      const batch = imageTitles.slice(i, i + batchSize);
      const params = new URLSearchParams({
        action: 'query',
        titles: batch.join('|'),
        prop: 'imageinfo',
        iiprop: 'url|size',
        iiurlwidth: optimalSize.toString(),
        format: 'json',
        origin: '*',
      });

      try {
        const apiUrl = `https://${wikiDomain}${WIKI_API_PATH}?${params.toString()}`;
        const resp = await fetch(apiUrl, { signal });

        if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
        if (!resp.ok) continue;

        const data = await resp.json();
        const pages = data.query?.pages || {};

        for (const pid in pages) {
          const ii = pages[pid].imageinfo?.[0];
          if (ii?.url) {
            const width = ii.thumbwidth || ii.width || 0;
            const height = ii.thumbheight || ii.height || 0;
            // Less strict size requirements
            if (width >= 80 && height >= 80) {
              urls.push({
                url: ii.url,                 // full-resolution for pop-up
                thumb: ii.thumburl || ii.url, // thumbnail for slider
                width,
                height
              });
            }
          }
        }
      } catch (err) {
        if (err.name === 'AbortError') throw err;
        console.warn(`fetchImageUrlsSimplified: Error in batch ${i/batchSize + 1}:`, err);
      }
    }

    return urls;
  }

  const handleSightClick = (sight) => {
    if (!sight) return;
    const articlePayload = {
      title: sight.wikiTitle || sight.articleTitle || sight.title,
      pageid: sight.id || sight.pageid || sight.link, // fallback unique id
      lat: sight.coords?.lat,
      lon: sight.coords?.lon,
      thumbnail: sight.thumbnail,
      fullurl: sight.link,
      originalDescription: sight.description, // Pass the original description
      isTopSight: true, // Mark as a top sight
    };

    const normalizeTopicName = (value) => String(value || '').trim().toLowerCase();
    const matchesLocationButtonTopic =
      normalizeTopicName(articlePayload.title) &&
      normalizeTopicName(cityName) &&
      normalizeTopicName(articlePayload.title) === normalizeTopicName(cityName);

    if (matchesLocationButtonTopic && locationData) {
      console.log('[DetailsCard] Sight/article matches location button topic – switching to location topic.');
      dispatch({ type: 'SET_LOCATION_MANUAL', payload: locationData });
      onArticleSelected(null);
      setShowLocationSights(true);
      setSightsLocationSnapshot(locationData);
      setSightsWikiLanguageSnapshot(viewportWikiLanguage);
      setSightsFromSearchfield(false);
      setSightsSearchFieldLocation(null);
      setSightsSearchFieldRequestRequired(false);
      return;
    }

    dispatch({ type: 'SET_ARTICLE', payload: articlePayload });
    onArticleSelected(articlePayload);
  };

  // Hide Top-Sights carousel whenever the user switches to an ARTICLE view so
  // that it does not auto-reappear the next time we fall back to a location.
  useEffect(() => {
    if (topic.type === 'article' && showLocationSights) {
      console.log('[DetailsCard] Switching to article – hiding Top-Sights carousel.');
      setShowLocationSights(false);
      setSightsLocationSnapshot(null);
    }
  }, [topic.type]);

  // Fetch article description in user's preferred language
  useEffect(() => {
    if (topic.type !== 'article' || !article?.title) {
      setArticleDescription('');
      setDescriptionLoading(false);
      setDescriptionError(null);
      return;
    }

    const getFirstSentence = (text) => {
      if (!text) return '';

      // Common abbreviations that shouldn't end a sentence
      const abbreviations = [
        'Mr', 'Mrs', 'Ms', 'Dr', 'Prof', 'Sr', 'Jr', 'St', 'Ave', 'Blvd', 'Rd', 'Co', 'Corp', 'Inc', 'Ltd',
        'vs', 'etc', 'i.e', 'e.g', 'cf', 'ca', 'c', 'approx', 'est', 'min', 'max', 'no', 'vol', 'p', 'pp',
        'U.S', 'U.K', 'U.N', 'E.U', 'N.Y', 'L.A', 'D.C', 'B.C', 'A.D', 'a.m', 'p.m', 'A.M', 'P.M'
      ];

      // Create regex pattern for abbreviations (case insensitive)
      const abbrevPattern = new RegExp(`\\b(${abbreviations.join('|')})\\.$`, 'i');

      let pos = 0;
      while (pos < text.length) {
        const periodIndex = text.indexOf('.', pos);
        if (periodIndex === -1) {
          // No more periods found, return the entire text
          return text.trim();
        }

        // Check what comes after the period
        const afterPeriod = text.substring(periodIndex + 1);

        // If period is at the end of text, this is likely the end of a sentence
        if (afterPeriod.trim() === '') {
          return text.substring(0, periodIndex + 1).trim();
        }

        // Check if this period is part of an enumeration (like "1. ", "a. ", "i. ")
        const beforePeriod = text.substring(0, periodIndex);
        const lastWord = beforePeriod.split(/\s+/).pop() || '';

        // Check for enumeration patterns
        if (/^[0-9]+$/.test(lastWord) || // numbered list: "1."
            /^[a-z]$/.test(lastWord) || // lettered list: "a."
            /^[ivxlcdm]+$/i.test(lastWord)) { // roman numerals: "i.", "ii.", etc.
          pos = periodIndex + 1;
          continue;
        }

        // Check if this is a known abbreviation
        if (abbrevPattern.test(beforePeriod.split(/\s+/).slice(-2).join(' '))) {
          pos = periodIndex + 1;
          continue;
        }

        // Check if the next character after period and any whitespace is uppercase
        // This is a strong indicator of a new sentence
        const nextChar = afterPeriod.trim().charAt(0);
        if (nextChar && /[A-Z]/.test(nextChar)) {
          return text.substring(0, periodIndex + 1).trim();
        }

        // If next character is lowercase, this is likely not the end of a sentence
        if (nextChar && /[a-z]/.test(nextChar)) {
          pos = periodIndex + 1;
          continue;
        }

        // Default case: treat as end of sentence
        return text.substring(0, periodIndex + 1).trim();
      }

      // No suitable sentence ending found, return the entire text
      return text.trim();
    };

    setArticleDescription('');
    setDescriptionLoading(true);
    setDescriptionError(null);

    // If it's a top sight and has an original description, use that directly.
    if (article.isTopSight && article.originalDescription) {
      console.log(`[DetailsCard/DescEffect] Using original description for Top Sight: "${article.title}"`);
      setArticleDescription(getFirstSentence(article.originalDescription));
      setDescriptionLoading(false);
      return;
    }

    // Proceed with Wikipedia fetching for regular articles or if top sight had no description
    const articleTitle = article.title;
    const currentArticleLang = article.sourceWikiLang || viewportWikiLanguage; // lang of the article source (e.g., 'de' for a German article)
    const userPrefLang = userPreferredWikiLangCode; // user's desired lang for description (e.g., 'en')

    const controller = new AbortController();
    const signal = controller.signal;

    console.log(`[DetailsCard/DescEffect] Article: "${articleTitle}" (lang: ${currentArticleLang}), UserPref: ${userPrefLang}`);

    if (currentArticleLang === userPrefLang) {
      // Article is already in user's preferred language
      console.log(`[DetailsCard/DescEffect] Fetching extract directly for "${articleTitle}" in ${userPrefLang}`);
      fetchArticleExtract(articleTitle, userPrefLang, signal)
        .then(extract => {
          if (signal.aborted) return;
          setArticleDescription(getFirstSentence(extract || ''));
        })
        .catch(err => {
          if (signal.aborted) return;
          console.error(`[DetailsCard/DescEffect] Error fetching extract for "${articleTitle}" in ${userPrefLang}:`, err);
          setDescriptionError('could not connect to description service');
        })
        .finally(() => {
          setDescriptionLoading(false);
        });
    } else {
      // Article is in a different language, check if user's preferred lang is available
      console.log(`[DetailsCard/DescEffect] Fetching langlinks for "${articleTitle}" (source: ${currentArticleLang}) to check for ${userPrefLang}`);
      fetchArticleLangLinks(articleTitle, currentArticleLang, signal)
        .then(langLinks => { // langLinks is like [{lang: 'de', title: 'Deutscher Titel'}, ...]
          const targetLangInfo = langLinks.find(link => link.lang === userPrefLang);
          if (targetLangInfo) {
            // User's preferred language is available
            console.log(`[DetailsCard/DescEffect] Found langlink for ${userPrefLang}. Title: "${targetLangInfo.title}". Fetching extract.`);
            fetchArticleExtract(targetLangInfo.title, userPrefLang, signal) // Use title from langlink
              .then(extract => {
                if (signal.aborted) return;
                setArticleDescription(getFirstSentence(extract || ''));
              })
              .catch(err => {
                if (signal.aborted) return;
                console.error(`[DetailsCard/DescEffect] Error fetching extract for "${targetLangInfo.title}" in ${userPrefLang}:`, err);
                setDescriptionError('could not connect to description service');
              })
              .finally(() => {
                setDescriptionLoading(false);
              });
          } else {
            // User's preferred language is not available
            console.log(`[DetailsCard/DescEffect] Article "${articleTitle}" (lang: ${currentArticleLang}) description not available in user's preferred language "${userPrefLang}". Available languages: ${langLinks.map(l => l.lang).join(', ') || 'none'}.`);
            setArticleDescription(''); // Clear description or set a message like "Description not available in [LanguageName]"
            setDescriptionError(null); // Not an error, just not available
            setDescriptionLoading(false);
          }
        })
        .catch(err => {
          // Failed to fetch langlinks
          console.error(`[DetailsCard/DescEffect] Failed to fetch language links for "${articleTitle}" (source: ${currentArticleLang}):`, err);
          setDescriptionError('could not connect to description service');
          setDescriptionLoading(false);
        });
    }
    return () => controller.abort();
  }, [article?.pageid, article?.title, article?.sourceWikiLang, viewportWikiLanguage, userPreferredWikiLangCode, topic.type]);

  // Function to handle SSE events from getRegionalDishes
  const handleFoodClick = async () => {
    if (!locationData) return;
    const locationName = getLocationName(locationData);

    console.log('[RegionalDishes] Starting food search for location:', locationName);

    flushSync(() => setFoodLoading(true));
    setFoodError(null);
    // Clear any previous dishes when the user activates the tab the first time for this topic
    if (foodDishes.length === 0) {
      setFoodDishes([]);
    }

    try {
      let idToken;
      try {
        loginMachine.recordUsage('llm');
        idToken = await loginMachine.getIdToken();
        if (!idToken) {
          throw new Error('user not authenticated');
        }
      } catch (error) {
        console.error('[RegionalDishes] Error fetching ID token:', error);
        setFoodError('could not connect to food service');
        flushSync(() => setFoodLoading(false));
        return;
      }

      // Construct the URL for the streamRegionalDishes Cloud Function
      const functionUrl = functionUrls.streamRegionalDishes;

      const encodedLocation = encodeURIComponent(locationName);

      const params = new URLSearchParams({
        location: encodedLocation,
        userLangLabel: userLanguageLabel,
        wikiLang: viewportWikiLanguage
      });
      // Add token as query param for EventSource
      params.append('authToken', idToken);

      const fullUrl = `${functionUrl}?${params.toString()}`;
      console.log('[RegionalDishes] Connecting to endpoint:', fullUrl);

      const eventSource = new EventSource(fullUrl);
      const seenTitles = new Set(); // Track seen titles for deduplication
      let messageCount = 0;
      let dishCount = 0;
      let duplicateCount = 0;

      eventSource.onopen = () => {
        console.log('[RegionalDishes] SSE Connection opened');
      };

      eventSource.onmessage = (event) => {
        messageCount++;
        console.log(`[RegionalDishes] Message ${messageCount} received:`, event.data);

        const raw = event.data.trim();
        if (raw === '[DONE]') {
          console.log('[RegionalDishes] Stream complete. Stats:', {
            totalMessages: messageCount,
            dishesReceived: dishCount,
            duplicatesSkipped: duplicateCount,
            uniqueDishes: seenTitles.size
          });
          flushSync(() => setFoodLoading(false));
          eventSource.close();
          return;
        }
        try {
          const data = JSON.parse(raw);
          dishCount++;
          flushSync(() => {
            setFoodDishes(prev => {
              const titleLower = data.title.toLowerCase();
              // dedupe against existing dishes in slider
              if (prev.some(d => d.title.toLowerCase() === titleLower)) {
                duplicateCount++;
                return prev;
              }
              console.log('[RegionalDishes] Adding new dish:', data.title);
              return [...prev, data];
            });
          });
        } catch (err) {
          console.error('[RegionalDishes] Failed to parse message:', err, event.data);
        }
      };

      eventSource.onerror = (error) => {
        if (eventSource.readyState === EventSource.CLOSED) {
          console.log('[RegionalDishes] SSE Connection closed by server.');
        } else {
          console.error('[RegionalDishes] SSE Error connecting to', fullUrl, error);
          // Check if user is not authenticated for contextual error message
          if (isGuestAccount) {
            setFoodError('AUTH_REQUIRED');
          } else {
            setFoodError('could not connect to food service');
          }
        }
        flushSync(() => setFoodLoading(false));
        eventSource.close();
      };

      // Cleanup function for component unmount
      return () => {
        console.log('[RegionalDishes] Cleaning up SSE connection');
        eventSource.close();
      };

    } catch (err) {
      console.error('[RegionalDishes] Critical error:', {
        error: err,
        message: err.message,
        stack: err.stack,
        locationData: locationData,
        params: {
          location: locationName,
          userLanguageLabel,
          viewportWikiLanguage
        }
      });

      // Check if it's an authentication error
      const isAuthError = err.message?.includes('not authenticated') ||
                         err.message?.includes('auth') ||
                         (err.response?.status === 401) ||
                         (err.response?.status === 403);

      if (isAuthError && isGuestAccount) {
        setFoodError('AUTH_REQUIRED');
      } else {
        setFoodError('could not connect to food service');
      }
      flushSync(() => setFoodLoading(false));
    }
  };

  // Helper to render control buttons - this will be passed to parent
  const periodsRef = useRef(null);
  const renderControls = useCallback(() => {


    return (
    <div className="control-section-horizontal">
      <button
        className="control-button control-button-overview"
        onClick={() => {
          const title = topic.type === 'article' ? topic.article.title : getLocationName(topic.locationData);
          const currentArticle = topic.type === 'article' ? topic.article : null;
          startNarration(title, 'overview', { topicId: topic.id }, locationData, currentArticle);
        }}
      >
        <i className="fas fa-volume-up button-icon" aria-hidden="true"></i>
        <span className="button-label">Overview</span>
      </button>

      <button
        className="control-button control-button-history"
        onClick={() => {
          const title = topic.type === 'article' ? topic.article.title : getLocationName(topic.locationData);
          const currentArticle = topic.type === 'article' ? topic.article : null;
          startNarration(title, 'anecdote', { topicId: topic.id }, locationData, currentArticle);
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <i className="fas fa-face-laugh button-icon" aria-hidden="true"></i>
          <i className="fas fa-volume-up button-icon" aria-hidden="true" style={{marginLeft: '4px'}}></i>
        </div>
        <span className="button-label">Anecdote</span>
      </button>

      <button
        className="control-button control-button-periods"
        onClick={handlePeriodsClick}
      >
        <i className="fas fa-list-ul button-icon" aria-hidden="true"></i>
        <span className="button-label">
          Historical
          <br />
          Eras
        </span>
      </button>

      <button
        className="control-button control-button-food"
        onClick={handleFoodClick}
      >
        <i className="fas fa-utensils button-icon" aria-hidden="true"></i>
        <span className="button-label">Local Food</span>
      </button>
    </div>
    );
  }, [topic, startNarration, handlePeriodsClick, handleFoodClick]);

  // Pass controls to parent component
  useEffect(() => {
    if (onControlsRender) {
      onControlsRender(renderControls);
    }

    // Cleanup function to clear controls when component unmounts
    return () => {
      if (onControlsRender) {
        onControlsRender(null);
      }
    };
  }, [renderControls, onControlsRender]);

  // Expose methods to parent
  useImperativeHandle(ref, () => ({
    handlePeriodsClick,
    handleFoodClick,
    // Allows parent (App) to force-switch the topic to a specific location – used when the
    // user selects a new place via the search bar so the DetailsCard updates instantly.
    switchToLocation: (locData) => {
      if (locData) {
        dispatch({ type: 'SET_LOCATION_MANUAL', payload: locData });
        // Also update the sights snapshot immediately for the new location
        setSightsLocationSnapshot(locData);
        setSightsWikiLanguageSnapshot(viewportWikiLanguage);
        setSightsFromSearchfield(fromSearchfield);
        setSightsSearchFieldLocation(searchFieldLocation);
        setSightsSearchFieldRequestRequired(searchFieldRequestRequired);
      }
    }
  }));

  // Function to load more images - SIMPLIFIED
  const loadMoreImages = useCallback(() => {
    if (imageLoadingMore || displayedImageCount >= images.length) return;

    setImageLoadingMore(true);

    // Simple timeout to prevent rapid calls
    setTimeout(() => {
      setDisplayedImageCount(prev => Math.min(prev + 10, images.length)); // Load 10 at a time for faster loading
      setImageLoadingMore(false);
    }, 200); // Reduced delay
  }, [imageLoadingMore, displayedImageCount, images.length]);

  // -------------------------
  // Tab specific render helpers (food & eras)
  // -------------------------

  const renderFoodTab = () => (
    <div className="food-section">
      <h3 className="section-heading">Local Food</h3>
      {foodLoading && (
        <ThinkingIndicator />
      )}
      {foodError && (
        foodError === 'AUTH_REQUIRED' ? (
          <div style={{
            padding: '15px 10px',
            textAlign: 'center',
            margin: '10px 0'
          }}>
            <button
              onClick={onSignInClick}
              style={{
                backgroundColor: '#22c55e',
                color: 'white',
                border: 'none',
                padding: '12px 20px',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'background-color 0.2s',
                width: '100%'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#16a34a'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#22c55e'}
            >
              Sign in to learn more
            </button>
          </div>
        ) : (
          <div style={{ color: '#666', padding: '10px' }}>{foodError}</div>
        )
      )}
      {foodDishes.length > 0 && !foodError && (
        <div className="food-slider slider-horizontal">
          {foodDishes.map((dish, i) => (
            <div key={i} className="food-card slider-card">
              <div className="food-card-thumbnail-container slider-card-thumbnail-container">
                <img src={dish.imagelink} alt={dish.title} className="food-thumb slider-card-image" />
                <div className="food-card-title-overlay slider-card-title-overlay">
                  <strong>{dish.title}</strong>
                </div>
              </div>
              <div className="food-card-description slider-card-description">
                <p>{dish.description}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderErasTab = () => (
    <div className="eras-section">
      <h3 className="section-heading">Historical Periods</h3>
      <HistoricalPeriods
        ref={periodsRef}
        topic={topic}
        topicId={topic.id}
        userLanguageLabel={userLanguageLabel}
        startNarration={startNarration}
        locationData={locationData}
        isGuestAccount={isGuestAccount}
        onSignInClick={onSignInClick}
      />
    </div>
  );

  // Helper to open Wikipedia article in an iframe with language fall-back
  const handleWikipediaTabClick = useCallback(() => {
    const userPrefLang = userPreferredWikiLangCode || 'en';

    // Helper to finalise URL selection
    const finaliseUrl = (langCode, titleForLang) => {
      const encodedTitle = encodeURIComponent(titleForLang.replace(/ /g, '_'));
      // Force Minerva skin and night mode to ensure dark theme is applied
      // `minervanightmode=1` is recognised by Wikimedia to enable dark mode regardless of user prefs.
      const url = `https://${langCode}.m.wikipedia.org/wiki/${encodedTitle}?minervanightmode=1`;
      setWikipediaUrl(url);
      setWikiIframeLoading(false);
    };

    if (topic.type === 'location') {
      const qid = locationTopicWikidataQid;
      if (!qid) {
        setWikipediaUrl(null);
        return;
      }

      setWikiIframeLoading(true);
      setWikiIframeError(null);

      const controller = new AbortController();
      const { signal } = controller;

      fetchWikidataSitelinks(qid, signal)
        .then((sitelinks) => {
          if (signal.aborted) return;

          const preferredOrder = Array.from(new Set([userPrefLang, 'en', viewportWikiLanguage]));
          for (const langCode of preferredOrder) {
            const link = sitelinks?.[`${langCode}wiki`];
            if (link?.title) {
              finaliseUrl(langCode, link.title);
              return;
            }
          }

          const anyWikipediaEntry = Object.entries(sitelinks || {}).find(([siteKey, value]) => {
            return siteKey.endsWith('wiki') && !siteKey.endsWith('wikivoyage') && value?.title;
          });

          if (anyWikipediaEntry) {
            const [siteKey, value] = anyWikipediaEntry;
            const langCode = siteKey.replace(/wiki$/, '') || 'en';
            finaliseUrl(langCode, value.title);
            return;
          }

          setWikiIframeError('Wikipedia page not available for this location');
          setWikiIframeLoading(false);
        })
        .catch((err) => {
          if (err.name === 'AbortError') return;
          console.error('[DetailsCard/Wikipedia] Wikidata sitelink fetch failed:', err);
          setWikiIframeError('could not connect to Wikipedia');
          setWikiIframeLoading(false);
        });
      return;
    }

    if (topic.type !== 'article' || !topic.article?.title) {
      setWikipediaUrl(null);
      return;
    }

    const articleTitle = topic.article.title;
    const sourceLang = topic.article.sourceWikiLang || viewportWikiLanguage; // language of the clicked article

    setWikiIframeLoading(true);
    setWikiIframeError(null);

    // Fast path – preferred language is same as source
    if (userPrefLang === sourceLang) {
      finaliseUrl(userPrefLang, articleTitle);
      return;
    }

    // Otherwise consult langlinks for availability
    const controller = new AbortController();
    const { signal } = controller;

    fetchArticleLangLinks(articleTitle, sourceLang, signal)
      .then((links) => {
        if (signal.aborted) return;

        const pref = links.find((l) => l.lang === userPrefLang);
        if (pref) {
          finaliseUrl(userPrefLang, pref.title);
          return;
        }

        const english = links.find((l) => l.lang === 'en');
        if (english) {
          finaliseUrl('en', english.title);
          return;
        }

        // Fall-back to original article language
        finaliseUrl(sourceLang, articleTitle);
      })
      .catch((err) => {
        if (err.name === 'AbortError') return;
        console.error('[DetailsCard/Wikipedia] Langlink fetch failed:', err);
        setWikiIframeError('could not connect to Wikipedia');
        finaliseUrl(sourceLang, articleTitle);
      });
  }, [locationTopicWikidataQid, topic, userPreferredWikiLangCode, viewportWikiLanguage]);

  // ------------------------
  // Wikipedia Tab renderer
  // ------------------------
  const renderWikipediaTab = () => (
    <div className="wikipedia-section" style={{ width: '100%', height: '70vh', position: 'relative' }}>
      {wikiIframeLoading && (
        <ThinkingIndicator style={{ padding: '10px' }} />
      )}
      {wikiIframeError && (
        <div style={{ color: '#666', padding: '10px' }}>{wikiIframeError}</div>
      )}
      {wikipediaUrl && !wikiIframeLoading && (
        <div className="wiki-dark-fallback" style={{ width: '100%', height: '100%', overflow: 'hidden', background: cardBgColor }}>
          <style>{wikipediaIframeStyles}</style>
          <iframe
            src={wikipediaUrl}
            title="Wikipedia Article"
            style={{
              border: 'none',
              width: '125%',      // Compensate for 0.8 scale so it fills width
              height: '144%',     // Extra height so full page is visible after scale
              transform: 'scale(0.8)',
              transformOrigin: '0 0'
            }}
            loading="lazy"
          />
        </div>
      )}
    </div>
  );

  const setThemeColour = (clr) => {
    let meta = document.querySelector('meta[name="theme-color"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.name = 'theme-color';
      document.head.appendChild(meta);
    }
    meta.content = clr;
  };

  // Custom styles for the Wikipedia iframe – match the card background and disable colour-inversion filter
  const wikipediaIframeStyles = `
    /* Ensure the iframe itself uses the same background as the card and show white text */
    .wiki-dark-fallback iframe {
      background: ${cardBgColor};
      color: #ffffff;
      /* Remove the old dark-mode inversion filter */
      filter: none !important;
      /* Hide internal scrollbars to avoid nested scroll views */
      scrollbar-width: none; /* Firefox */
      -ms-overflow-style: none; /* IE/Edge */
    }
    .wiki-dark-fallback iframe::-webkit-scrollbar {
      display: none; /* WebKit */
    }
  `;

  // Add helper flag for wiki tab
  const isWikiTabActive = activeTab === 'wikipedia-tab';

  return (
    <div
      ref={cardRef}
      className="details-card"
      onPointerDownCapture={onCardPointerDown} /* NEW: capture full-card pointer down */
      style={{ height: heightPx, transition: draggingRef.current ? 'none' : 'height 0.25s ease' }}
    >
      <div className="drag-bar" onPointerDown={onCardPointerDown} />
      <div className="details-card-header" onPointerDown={onCardPointerDown}>
        <div className="details-card-header-main-row">
          {/* Title takes available space, nav buttons go to the right due to flex */}
          <h3 className="details-title" style={{ marginRight: '10px' }}>
            {topic.type === 'article' && topic.article?.pageid === 'prompt'
              ? 'How to use Curio:'
              : topic.type === 'location'
                ? (getLocationName(topic.locationData) || 'Current Location')
                : topic.article.title}
          </h3>
          {/* Population badge to the right of the title */}
          {topic.type === 'location' && locationTopicWikidataQid && (
            <span className="header-population-badge">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M20 21a8 8 0 0 0-16 0" />
                <circle cx="12" cy="7" r="4" />
              </svg>
              <span>{locationPopulationLoading ? '…' : (formatPopulationCompact(locationPopulation) || '—')}</span>
            </span>
          )}
        </div>
      </div>
      {/* ------------ New Card Body ------------ */}
      <div className="details-card-content">
        {/* Full width content - no more left/right split */}
        <div className={`content-column ${showCardLoginPrompt ? 'content-column--login-blurred' : ''}`} ref={contentRef}>
          {/* Special prompt UI when GPS is not available */}
          {topic.type === 'article' && topic.article?.pageid === 'prompt' && (
            <main className="info-prompt-main">
              <div className="info-prompt-grid">
                {/* Row 1 */}
                <div className="info-prompt-number">1.</div>
                <div className="info-prompt-content">
                  <h2 className="info-prompt-title">Zoom to your area of interest!</h2>
                </div>

                {/* Row 2 */}
                <div className="info-prompt-number" style={{alignSelf: 'center'}}>2.</div>
                <div className="info-prompt-content">
                  <div className="info-prompt-cards-with-or">
                    <div className="info-prompt-card">
                      <span className="material-icons info-prompt-icon info-prompt-icon-orange">map</span>
                      <p className="info-prompt-card-text">
                        Click map button to learn about a region.
                      </p>
                    </div>
                    <div className="info-prompt-or">or</div>
                    <div className="info-prompt-card">
                      <span className="material-icons info-prompt-icon info-prompt-icon-rose">place</span>
                      <p className="info-prompt-card-text">
                        Click on markers to learn about POIs.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </main>
          )}

          {/* ---- STICKY NAV BAR (two-group tabs) ----------------------------- */}
          <nav className="details-sticky-nav">
            {!(topic.type === 'article' && topic.article?.pageid === 'prompt') && (
            <>
              {/* Tab group selector row */}
              <div className="tab-group-selector">
                <button
                  className={`tab-group-btn ${activeTabGroup === 'audio' ? 'active' : ''}`}
                  onClick={() => setActiveTabGroup('audio')}
                >
                  <i className="fas fa-headphones tab-group-icon" aria-hidden="true"></i>
                  Audio Guides
                </button>
                <button
                  className={`tab-group-btn ${activeTabGroup === 'more' ? 'active' : ''}`}
                  onClick={() => setActiveTabGroup('more')}
                >
                  <i className="fas fa-ellipsis-h tab-group-icon" aria-hidden="true"></i>
                  More
                </button>
              </div>

              {/* Audio Guides panel */}
              {activeTabGroup === 'audio' && (
                <div className="details-nav-row">
                  {[
                    { id: 'overview-tab', icon: ['fas fa-volume-up'], label: 'Overview', colorClass: 'nav-clr-overview' },
                    { id: 'anecdote-tab', icon: ['fas fa-volume-up', 'fas fa-face-laugh'], label: 'Anecdote', colorClass: 'nav-clr-history' },
                  ].map(tab => (
                    <button
                      key={tab.id}
                      className={`nav-tab ${tab.colorClass}`}
                      onClick={() => {
                        const title = topic.type === 'article' ? topic.article.title : getLocationName(topic.locationData);
                        const currentArticle = topic.type === 'article' ? topic.article : null;
                        if (tab.id === 'overview-tab') {
                          startNarration(title, 'overview', { topicId: topic.id }, locationData, currentArticle);
                        } else if (tab.id === 'anecdote-tab') {
                          startNarration(title, 'anecdote', { topicId: topic.id }, locationData, currentArticle);
                        }
                      }}
                    >
                      <div className="nav-tab-icons">
                        {tab.icon.map((cls, idx) => (
                          <i key={idx} className={`${cls} nav-icon`}></i>
                        ))}
                      </div>
                      <span className="nav-tab-label">{tab.label}</span>
                    </button>
                  ))}
                </div>
              )}

              {/* More panel */}
              {activeTabGroup === 'more' && (
                <div className="details-nav-row">
                  {[
                    { id: 'eras-tab', icon: ['fas fa-list-alt'], label: 'Historical Eras', colorClass: 'nav-clr-eras' },
                    { id: 'food-tab', icon: ['fas fa-utensils'], label: 'Local Food', colorClass: 'nav-clr-food', hideForArticle: true },
                    { id: 'wikipedia-tab', icon: ['fas fa-globe'], label: 'Wiki', colorClass: 'nav-clr-wiki' },
                  ]
                    .filter(tab => {
                      if (tab.hideForArticle && topic.type === 'article') return false;
                      if (tab.id === 'wikipedia-tab' && topic.type !== 'article' && topic.type !== 'location') return false;
                      return true;
                    })
                    .map(tab => {
                      const isWikiDisabled = tab.id === 'wikipedia-tab' && topic.type === 'location' && !hasLocationWikiAvailable;
                      return (
                        <button
                          key={tab.id}
                          className={`nav-tab ${tab.colorClass} ${activeTab === tab.id ? 'active' : ''}`}
                          disabled={isWikiDisabled}
                          aria-disabled={isWikiDisabled}
                          title={isWikiDisabled ? 'Wikipedia link not available for this location yet' : undefined}
                          style={isWikiDisabled ? { opacity: 0.45, cursor: 'not-allowed' } : undefined}
                          onClick={() => {
                            if (isWikiDisabled) return;
                            setActiveTab(tab.id);
                            switch (tab.id) {
                              case 'eras-tab':
                                handlePeriodsClick();
                                break;
                              case 'food-tab':
                                handleFoodClick();
                                break;
                              case 'wikipedia-tab':
                                handleWikipediaTabClick();
                                break;
                              default:
                                break;
                            }
                          }}
                        >
                          <div className="nav-tab-icons">
                            {tab.icon.map((cls, idx) => (
                              <i key={idx} className={`${cls} nav-icon`}></i>
                            ))}
                          </div>
                          <span className="nav-tab-label">{tab.label}</span>
                        </button>
                      );
                    })}
                </div>
              )}
            </>
            )}
            {/* Audio widget below the tab row */}
            {audioEnabled && audioStatus !== 'idle' && (
              <div
                ref={audioWidgetAnchorRef}
                className="details-nav-audio-widget"
                onPointerDown={(e) => e.stopPropagation()}
              >
                <AudioStatusWidget
                  embedded
                  status={audioStatus}
                  title={audioTitle}
                  mode={audioMode}
                  onPauseToggle={onAudioPauseToggle}
                  onTogglePlayPause={onAudioTogglePlayPause}
                  onStop={onAudioStop}
                  onSeek={onAudioSeek}
                  currentTime={audioCurrentTime}
                  duration={audioDuration}
                />
              </div>
            )}
          </nav>
          {/* ------------------------------------------------------------------------ */}



          {/* Conditional Image Slider for Articles (not for prompt) */}
          {topic.type === 'article' && topic.article?.pageid !== 'prompt' && images.length > 0 && !isWikiTabActive && (
            <div className="image-slider-container" onPointerDown={onCardPointerDown}>
              {images.slice(0, displayedImageCount).map((img, i) => (
                <img
                  key={i}
                  src={img.thumb || img.url}
                  alt={`Article image ${i + 1}`}
                  className="slider-image"
                  loading="lazy"
                  onClick={() => handleImageClick(i)}
                  onError={(e) => {
                    // Hide broken images
                    e.target.style.display = 'none';
                  }}
                />
              ))}
              {displayedImageCount < images.length && (
                <div className="load-more-images-container">
                  <button
                    className="load-more-images-btn"
                    onClick={loadMoreImages}
                    disabled={imageLoadingMore}
                    style={{
                      padding: '8px 16px',
                      background: 'rgba(0,0,0,0.7)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: imageLoadingMore ? 'not-allowed' : 'pointer',
                      opacity: imageLoadingMore ? 0.6 : 1,
                      minWidth: '120px'
                    }}
                  >
                    {imageLoadingMore ? 'Loading...' : `Load ${Math.min(10, images.length - displayedImageCount)} more`}
                  </button>
                </div>
              )}
              {imagesLoading && (
                <ThinkingIndicator style={{ padding: '10px', fontSize: '0.9em' }} />
              )}
            </div>
          )}

          {/* Show loading state for articles without images yet (not for prompt) */}
          {topic.type === 'article' && topic.article?.pageid !== 'prompt' && images.length === 0 && imagesLoading && !isWikiTabActive && (
            <div className="image-slider-container" onPointerDown={onCardPointerDown}>
              <ThinkingIndicator style={{ padding: '20px', fontSize: '0.9em' }} />
            </div>
          )}

          {/* Conditional Horizontal Sights Carousel for Locations */}
          {topic.type === 'location' && !showLocationSights && (
            <div className="tap-to-learn-more-hint" style={{ padding: '8px', textAlign: 'center', color: '#888' }}>
              Tap to learn more
            </div>
          )}
          {topic.type === 'location' && showLocationSights && !isWikiTabActive && activeTab !== 'food-tab' && activeTab !== 'eras-tab' && (
            <TopSightsCarousel
              // Pass the snapshots so internal fetches happen only after a new click
              locationData={sightsLocationSnapshot}
              viewportWikiLanguage={sightsWikiLanguageSnapshot}
              userLanguageLabel={userLanguageLabel}
              onSightsReady={onSightsReady}
              onSightSelect={handleSightClick}
              onPointerDown={onCardPointerDown}
              fromSearchfield={sightsFromSearchfield}
              searchFieldLocation={sightsSearchFieldLocation}
              searchFieldRequestRequired={sightsSearchFieldRequestRequired}
              onSearchFieldRequestCompleted={() => {
                setSightsFromSearchfield(false);
                setSightsSearchFieldLocation(null);
                setSightsSearchFieldRequestRequired(false);
                onSearchFieldRequestCompleted();
              }}
            />
          )}

          {/* Article Description Display - Below sliders (not for prompt) */}
          {topic.type === 'article' && article?.title && topic.article?.pageid !== 'prompt' && (
            <div
              className="article-description-container"
              style={{
                padding: '8px 2px 8px 2px', /* Aligned with carousel/nav horizontal spacing */
                fontSize: '0.75em',
                lineHeight: '1.4'
              }}
            >
              {descriptionLoading && (
                <ThinkingIndicator style={{ justifyContent: 'flex-start', padding: 0, fontSize: '1em' }} />
              )}
              {descriptionError && <p style={{ color: '#666', margin: 0 }}>{descriptionError}</p>}
              {!descriptionLoading && !descriptionError && articleDescription && (
                <p className="article-description" style={{margin: 0}}>{articleDescription}</p>
              )}
            </div>
          )}

          {/* ---------------- TAB CONTENT AREA (food / eras) ---------------- */}
          <div className="tab-content-area">
            {activeTab === 'eras-tab' && renderErasTab()}
            {activeTab === 'food-tab' && renderFoodTab()}
            {activeTab === 'wikipedia-tab' && renderWikipediaTab()}

          </div>
        </div>
        {showCardLoginPrompt && (
          <SigninPopup
            onClose={() => loginMachine.closePrompt()}
            isAnonymous={isLoginAnonymous}
            error={loginUi.kind === 'DetailsCardLoginPrompt' ? loginUi.error : undefined}
            cardOverlay
          />
        )}
      </div>

      {(showPwaInstallButton || onOpenUserHistory) && (
        <div style={{
          position: 'absolute',
          left: '16px',
          bottom: '100%',
          marginBottom: '12px',
          zIndex: 9999,
          pointerEvents: 'none',
          display: 'flex',
          flexDirection: 'column',
          gap: '10px',
          alignItems: 'flex-start',
        }}>
          {showPwaInstallButton && (
            <button
              onClick={onPwaInstallClick}
              style={{
                background: 'rgba(50, 50, 50, 0.85)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                padding: 0,
                borderRadius: '50%',
                cursor: 'pointer',
                boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                lineHeight: '1',
                color: '#f0f0f0',
                width: '38px',
                height: '38px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                pointerEvents: 'auto',
              }}
              aria-label="Install app"
              title="Install app"
            >
              <i className="fas fa-download" style={{ fontSize: '14px' }}></i>
            </button>
          )}
          {onOpenUserHistory && (
            <button
              ref={historyButtonRef}
              onClick={onOpenUserHistory}
              style={{
                background: 'rgba(50, 50, 50, 0.85)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                padding: 0,
                borderRadius: '50%',
                cursor: 'pointer',
                boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                lineHeight: '1',
                color: '#f0f0f0',
                width: '38px',
                height: '38px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                pointerEvents: 'auto',
              }}
              aria-label="User history"
            >
              <i className="fas fa-history" style={{ fontSize: '14px' }}></i>
            </button>
          )}
        </div>
      )}

      {/* Floating buttons above the card: Get Pro + GPS + Location */}
      <div style={{
        position: 'absolute',
        right: '16px',
        bottom: '100%',
        marginBottom: '12px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        gap: '10px',
        zIndex: 9999,
        pointerEvents: 'none', /* let map touches pass through gaps */
      }}>
        {/* Get Pro Button */}
        <button
          className="get-pro-button"
          onClick={() => {
            if (isGuestAccount) {
              loginMachine.requestLogin('manual', { preferQuickPrompt: false });
            } else {
              setShowQuotaModal(true);
            }
          }}
          style={{
            width: '38px',
            height: '38px',
            borderRadius: '50%',
            backgroundColor: '#4CAF50',
            color: 'white',
            border: 'none',
            boxShadow: '0 2px 5px rgba(0,0,0,0.3)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'background-color 0.3s ease, transform 0.3s ease',
            pointerEvents: 'auto',
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = '#45a049';
            e.target.style.transform = 'scale(1.1)';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = '#4CAF50';
            e.target.style.transform = 'scale(1)';
          }}
          aria-label="Get Pro"
        >
          <span className="material-icons" style={{ fontSize: '22px' }}>add</span>
        </button>

        {/* Map kind button – below Get Pro */}
        {onToggleMapType && (
          <button
            onClick={onToggleMapType}
            style={{
              background: 'rgba(50, 50, 50, 0.85)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              padding: 0,
              borderRadius: '50%',
              cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
              lineHeight: '1',
              color: '#f0f0f0',
              width: '38px',
              height: '38px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              pointerEvents: 'auto',
            }}
            aria-label="Toggle map type"
          >
            <i className="fas fa-layer-group" style={{ fontSize: '14px' }}></i>
          </button>
        )}

        {/* Go to My Location button */}
        {onGoToMyLocation && (
          <button
            onClick={onGoToMyLocation}
            style={{
              background: 'rgba(50, 50, 50, 0.85)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              padding: 0,
              borderRadius: '50%',
              cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
              lineHeight: '1',
              color: '#f0f0f0',
              width: '38px',
              height: '38px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              pointerEvents: 'auto',
            }}
            aria-label="Go to my location"
            dangerouslySetInnerHTML={{ __html: isMapCenteredOnUser
              ? `<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24">
                  <g>
                    <circle cx="12" cy="12" r="5" fill="#4285F4" stroke="white" stroke-width="2"/>
                    <circle cx="12" cy="12" r="9" stroke="#4285F4" stroke-width="2" opacity="0.3" fill="none"/>
                  </g>
                </svg>`
              : `<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24">
                  <g fill="none" stroke="white" stroke-width="2">
                    <circle cx="12" cy="12" r="5" stroke="white"/>
                    <circle cx="12" cy="12" r="9" opacity="0.4"/>
                  </g>
                </svg>`
            }}
          />
        )}

        {/* Location button – below GPS */}
        {locationData && (
          <button
            className="floating-location-btn"
            onClick={() => {
              console.log('[DetailsCard] Location button pressed – switching to location topic.');
              if (locationData && locationData.lat && locationData.lon) {
                setLastButtonPressPosition({
                  lat: locationData.lat,
                  lon: locationData.lon
                });
              }
              dispatch({ type: 'SET_LOCATION_MANUAL', payload: locationData });
              onArticleSelected(null);
              setShowLocationSights(true);
              setSightsLocationSnapshot(locationData);
              setSightsWikiLanguageSnapshot(viewportWikiLanguage);
              setSightsFromSearchfield(false);
              setSightsSearchFieldLocation(null);
              setSightsSearchFieldRequestRequired(false);
            }}
            aria-label={`Location: ${cityName || 'Current Location'}`}
            style={{ pointerEvents: 'auto' }}
          >
            <div style={{ position: 'relative', display: 'inline-flex' }}>
              <span className="material-icons" style={{ fontSize: '18px' }} aria-hidden="true">map</span>
              {hasMovedSignificantly() && (
                <div
                  style={{
                    position: 'absolute',
                    bottom: '-2px',
                    right: '-2px',
                    width: '8px',
                    height: '8px',
                    backgroundColor: 'limegreen',
                    borderRadius: '50%',
                    zIndex: 1
                  }}
                />
              )}
            </div>
            <span className="floating-location-label">{cityName || 'Location'}</span>
            <i className="fas fa-chevron-right" style={{ fontSize: '0.65rem', opacity: 0.7 }} aria-hidden="true"></i>
          </button>
        )}
      </div>

      {/* Quota Modal */}
      {showQuotaModal && (
        <QuotaModal onClose={() => setShowQuotaModal(false)} />
      )}

      {/* Image Popup Modal */}
      {isPopupVisible && images.length > 0 && (
        <div className="image-popup-overlay" onClick={handlePopupClose}>
          <div className="image-popup-content" onClick={(e) => e.stopPropagation()}>
            <button className="popup-close-btn" onClick={handlePopupClose}>&times;</button>
            <button className="popup-nav-btn popup-prev-btn" onClick={handlePopupPrev}>&lt;</button>
            <img
              src={getPopupImageUrl(images[popupImageIndex])}
              alt={`Popup image ${popupImageIndex + 1}`}
              className="popup-image"
            />
            <button className="popup-nav-btn popup-next-btn" onClick={handlePopupNext}>&gt;</button>
          </div>
        </div>
      )}
    </div>
  );
});

export default DetailsCard;
